/* istanbul ignore file */
import React, { useCallback, useMemo } from 'react';
import { getImageDetailAsync } from '@bingads-webui-campaign/image-experience-utils';
import { useImagePicker } from '@bingads-webui-campaign-react/add-image-hooks';
import { UploadImage } from '@bingads-webui-campaign-react/display-ads-editor';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { CSMLAssetSource } from '@bingads-webui-campaign-react/asset-library-model';
import PropTypes from 'prop-types';
import { ListPanel } from '../list-panel';
import { BrandKitLogoEditPanel } from '../logo-edit-panel';
import { duplicateImagesValidation, imageMinSizeValidation, processImageAssetPayload } from './process-image-asset-payload';
// import { BrandKitImageEditPanel } from '../image-edit-panel';

export const ImageListElement = React.memo(({
  data,
  title,
  i18n,
  maxLimit,
  maxCount,
  isLoading,
  maxSquareLogosLimit,
  maxLandscapeLogosLimit,
  elementType,
  subTitle,
  text,
  contentBefore,
  preferenceService,
  permissions,
  serverContext,
  aid,
  cid,
  panelTitle,
  panelSubtitle,
  showEditBtn,
  oDataConfig,
  perfMarker,
  updateOrCreateBrandKit,
  helpTopic,
  helpLabel,
}) => {
  const [openLogoEditPanel, setOpenLogoEditPanel] = React.useState(false);
  const [selectedImageIdx, setSelectedImageIdx] = React.useState(0);
  const [errorMessage, setErrorMessage] = React.useState('');
  const {
    displayImagePicker,
    setDisplayImagePicker,
    selectedMediaPanelSource,
    setSelectedMediaPanelSource,
  } = useImagePicker();
  const clearErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);
  const minSizeConfig = constants.brandkitMinSize;
  const selectedImage = useMemo(() => data[selectedImageIdx] || {}, [data, selectedImageIdx]);
  const openImageEditPanel = useCallback((idx) => {
    setSelectedImageIdx(idx);
    if (elementType === 'logo') {
      setOpenLogoEditPanel(true);
    }
    clearErrorMessage();
  }, [clearErrorMessage, elementType]);
  const onEditImage = useCallback((ev) => {
    const { idx } = ev.currentTarget.dataset;
    const index = Number(idx);
    if (data && data[index]) {
      if (data[index].Width && data[index].Height) {
        openImageEditPanel(index);
        return;
      }
      const fullUrl = data[index].Url;
      getImageDetailAsync(fullUrl).then(({ naturalWidth, naturalHeight }) => {
        const newData = data;
        newData[index].Width = naturalWidth;
        newData[index].Height = naturalHeight;
        openImageEditPanel(index);
      });
    }
  }, [data, openImageEditPanel]);
  const onAddImage = useCallback(() => {
    setDisplayImagePicker(true);
    clearErrorMessage();
  }, [clearErrorMessage, setDisplayImagePicker]);
  const onRemoveImage = (ev) => {
    const { idx } = ev.currentTarget.dataset;
    let key = 'images';
    if (elementType === 'logo') {
      key = 'logos';
    }
    updateOrCreateBrandKit({
      [key]: data.filter((_, i) => i !== Number(idx)),
    });
    clearErrorMessage();
  };

  const closeLogoEditPanel = useCallback(() => {
    setOpenLogoEditPanel(false);
  }, []);
  const onSelectImages = useCallback((imageData) => {
    const payload = processImageAssetPayload({ images: imageData, elementType, data });
    const { hasError } = imageMinSizeValidation(payload, minSizeConfig, elementType);
    const { data: imageAsset, hasDuplicateError } = duplicateImagesValidation(payload, elementType);
    if (hasError) {
      setErrorMessage(_TL_('Image does not meet the minimum pixel requirements for this placement.'));
    } else if (hasDuplicateError) {
      setErrorMessage(_TL_('Image is duplicate'));
    } else { clearErrorMessage(); }
    updateOrCreateBrandKit(imageAsset);
  }, [clearErrorMessage, data, elementType, minSizeConfig, updateOrCreateBrandKit]);

  const onApplyEditLogo = useCallback((image) => {
    if (image && image.cropSettings) {
      const newData = data;
      newData[selectedImageIdx].CropX = image.cropSettings.SourceX;
      newData[selectedImageIdx].CropY = image.cropSettings.SourceY;
      newData[selectedImageIdx].CropWidth = image.cropSettings.SourceWidth;
      newData[selectedImageIdx].CropHeight = image.cropSettings.SourceHeight;
      newData[selectedImageIdx].ImageType = image.aspectRatio;
      updateOrCreateBrandKit({
        logos: data,
      });
    }
    closeLogoEditPanel();
  }, [closeLogoEditPanel, data, selectedImageIdx, updateOrCreateBrandKit]);

  const cropSettings = useMemo(() => ({
    SourceX: selectedImage.CropX,
    SourceY: selectedImage.CropY,
    SourceWidth: selectedImage.CropWidth,
    SourceHeight: selectedImage.CropHeight,
  }), [selectedImage]);

  return (
    <>
      <ListPanel
        images={data}
        title={title}
        isLoading={isLoading}
        subTitle={subTitle}
        text={text}
        errorMessage={errorMessage ? i18n.getString(errorMessage) : ''}
        showEditBtn={showEditBtn}
        maxCount={maxCount}
        helpTopic={helpTopic}
        helpLabel={helpLabel}
        headerProps={{ contentBefore }}
        onAdd={onAddImage}
        onEdit={onEditImage}
        onRemove={onRemoveImage}
      />
      {displayImagePicker && (
        <UploadImage
          displayImagePicker={displayImagePicker}
          setDisplayImagePicker={setDisplayImagePicker}
          selectedMediaPanelSource={selectedMediaPanelSource}
          setSelectedMediaPanelSource={setSelectedMediaPanelSource}
          i18n={i18n}
          minSizeConfig={minSizeConfig}
          isBrandKit
          panelTitle={panelTitle}
          panelSubtitle={panelSubtitle}
          maxLimit={maxLimit}
          elementType={elementType}
          onSelectImages={onSelectImages}
          preferenceService={preferenceService}
          permissions={permissions}
          serverContext={serverContext}
          aid={aid}
          cid={cid}
          oDataConfig={oDataConfig}
          perfMarker={perfMarker}
          disabledAssetSources={[CSMLAssetSource.Shutterstock]}
        />
      )}
      {openLogoEditPanel && (
        <BrandKitLogoEditPanel
          i18n={i18n}
          minLogoSize={128}
          hasLandScapeLimit={maxLandscapeLogosLimit > 0}
          hasSquareLimit={maxSquareLogosLimit > 0}
          isOpen={openLogoEditPanel}
          dismiss={closeLogoEditPanel}
          image={selectedImage}
          format={selectedImage.ImageType}
          onChange={onApplyEditLogo}
          cropSettings={cropSettings}
        />
      )}
      {/* { openImageEditPanel &&
        <BrandKitImageEditPanel
          i18n={i18n}
          isOpen={openImageEditPanel}
          dismiss={closeImageEditPanel}
          sourceImage={editImage}
          onChange={onApplyEditImage}
          cropSettings={cropSettings}
          removeImageBackground={removeImageBackground}
        />
      } */}
    </>
  );
});


ImageListElement.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  title: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  subTitle: PropTypes.string.isRequired,
  text: PropTypes.string,
  maxLimit: PropTypes.number,
  panelTitle: PropTypes.string,
  panelSubtitle: PropTypes.string,
  contentBefore: PropTypes.element.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  updateOrCreateBrandKit: PropTypes.func.isRequired,
  elementType: PropTypes.oneOf(['logo', 'image']).isRequired,
  aid: PropTypes.number.isRequired,
  maxSquareLogosLimit: PropTypes.number,
  maxLandscapeLogosLimit: PropTypes.number,
  cid: PropTypes.number.isRequired,
  maxCount: PropTypes.number.isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  serverContext: PropTypes.shape({}),
  showEditBtn: PropTypes.bool,
  helpTopic: PropTypes.string,
  helpLabel: PropTypes.string,
};

ImageListElement.defaultProps = {
  serverContext: {},
  data: [],
  text: '',
  maxSquareLogosLimit: 5,
  maxLandscapeLogosLimit: 5,
  panelTitle: '',
  panelSubtitle: '',
  maxLimit: 10,
  showEditBtn: true,
  helpTopic: '',
  helpLabel: '',
};
